import type { StaffRouter } from '@eklevo/api/staff-trpc'
import { DEFAULT_BRAND, normalizeHexColor } from '@eklevo/shared/brand'
import { COMPANY_LEGAL_FIELDS } from '@eklevo/shared/company'
import type { TimeZone } from '@eklevo/shared/domain'
import type { Locale } from '@eklevo/shared/i18n/backoffice'
import type { inferRouterInputs } from '@trpc/server'
import {
  contrastProblems,
  legalPayload,
  legalProblems,
  legalValues,
  lineProblem,
  WIZARD_LIMITS,
  type LegalDraft,
  type LogoDraft,
} from './company-wizard'
import type { CompanyDetail } from './screens'

/*
 * «Editar» a company (ADR 0009 §8), its legal data too (decided on 2026-10-18): the form's draft,
 * which starts from the company's detail and lives only in the page; the checks the page makes
 * before asking for the passkey (the same rules as the server's, packages/core's checkCompanyData,
 * which answers each problem at its field's path anyway); `companies.update`'s payload; and where
 * each problem the server answers belongs.
 * The fields are named as the wizard's first step names them, so that the wizard's messages say
 * what is wrong with each (errorMessage, company-wizard.ts).
 */

/**
 * The logo of the form: the company's, as it is (`kept`); a new one, uploaded from the browser as
 * soon as it is chosen (ADR 0009 §6) and kept by its upload's token once clean; or none (`null`),
 * which takes away the company's on saving.
 */
export type EditLogo = { status: 'kept' } | LogoDraft | null

export interface EditDraft extends LegalDraft {
  name: string
  defaultLocale: Locale
  timeZone: TimeZone
  brand: { enabled: boolean; primary: string; accent: string }
  logo: EditLogo
}

export type CompanyChangePayload = inferRouterInputs<StaffRouter>['companies']['update']

/** The form as the company is now. */
export function editDraftOf(company: CompanyDetail): EditDraft {
  return {
    name: company.name,
    legalName: company.legalName ?? '',
    taxId: company.taxId ?? '',
    address: company.address ?? '',
    privacyContact: company.privacyContact ?? '',
    defaultLocale: company.defaultLocale,
    timeZone: company.timeZone,
    brand: company.brand
      ? { enabled: true, ...company.brand }
      : { enabled: false, primary: DEFAULT_BRAND.primary, accent: DEFAULT_BRAND.accent },
    logo: company.logo ? { status: 'kept' } : null,
  }
}

/** The colours the form would save: both normalized, or none. */
function brandOf(draft: EditDraft): { primary: string; accent: string } | null {
  if (!draft.brand.enabled) return null
  return {
    primary: normalizeHexColor(draft.brand.primary) ?? draft.brand.primary,
    accent: normalizeHexColor(draft.brand.accent) ?? draft.brand.accent,
  }
}

/**
 * What `companies.update` receives: the data as typed, which the server trims and checks; the
 * logo, absent while it is the company's as it was, its upload's token once a new one is clean,
 * or null to take the company's away; and the version of the company's data the form started from
 * (`company`, as the form found it), which the server refuses if they changed meanwhile.
 */
export function toChange(draft: EditDraft, company: CompanyDetail): CompanyChangePayload {
  const payload: CompanyChangePayload = {
    name: draft.name,
    ...legalPayload(draft),
    defaultLocale: draft.defaultLocale,
    timeZone: draft.timeZone,
    brand: brandOf(draft),
    version: company.version,
  }
  if (draft.logo?.status === 'ready') payload.logo = { upload: draft.logo.token }
  else if (draft.logo === null && company.logo) payload.logo = null
  return payload
}

/** Whether saving the form would change anything of the company. */
export function editChanges(draft: EditDraft, company: CompanyDetail): boolean {
  const brand = brandOf(draft)
  const legal = legalValues(draft)
  return (
    draft.name.trim() !== company.name ||
    COMPANY_LEGAL_FIELDS.some((field) => legal[field] !== company[field]) ||
    draft.defaultLocale !== company.defaultLocale ||
    draft.timeZone !== company.timeZone ||
    brand?.primary !== company.brand?.primary ||
    brand?.accent !== company.brand?.accent ||
    draft.logo?.status === 'ready' ||
    draft.logo?.status === 'uploading' ||
    (draft.logo === null && company.logo)
  )
}

/** The problems the page finds before saving, by field (the server checks it all again). */
export function checkEdit(draft: EditDraft): Record<string, string> {
  const errors: Record<string, string> = {}
  const name = lineProblem(draft.name.trim(), WIZARD_LIMITS.name.max, WIZARD_LIMITS.name.min)
  if (name) errors.name = name
  Object.assign(errors, legalProblems(draft))
  // A logo still going: the company would be saved without it.
  if (draft.logo?.status === 'uploading') errors.logo = 'uploading'
  if (draft.brand.enabled) {
    const primary = normalizeHexColor(draft.brand.primary)
    const accent = normalizeHexColor(draft.brand.accent)
    if (!primary) errors['brand.primary'] = 'invalid'
    if (!accent) errors['brand.accent'] = 'invalid'
    if (primary && accent) Object.assign(errors, contrastProblems(primary, accent))
  }
  return errors
}

const FIELDS: ReadonlySet<string> = new Set([
  'name',
  'defaultLocale',
  'timeZone',
  'logo',
  ...COMPANY_LEGAL_FIELDS,
])

/** The server's problems, by field; how many were of a path the page does not know. */
export function editIssuesToErrors(
  issues: readonly { path: (string | number)[]; code: string }[],
): {
  errors: Record<string, string>
  unknown: number
} {
  const errors: Record<string, string> = {}
  let unknown = 0
  for (const { path, code } of issues) {
    const [root, second] = path
    const field =
      typeof root === 'string' && FIELDS.has(root) && path.length === 1
        ? root
        : root === 'brand' && (second === 'primary' || second === 'accent') && path.length === 2
          ? `brand.${second}`
          : null
    if (field) errors[field] ??= code
    else unknown++
  }
  return { errors, unknown }
}

/**
 * The problems of the fields the form shows: those of the colours, while they are not used, are
 * kept aside, as their fields are.
 */
export function shownEditErrors(draft: EditDraft, errors: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(errors).filter(([field]) => draft.brand.enabled || !field.startsWith('brand.')),
  )
}
